function hasValidationErrors(snapshot) {
    const errors = snapshot?.memo?.errors;
    if (!errors) {
        return false;
    }
    return Array.isArray(errors) ? errors.length > 0 : Object.keys(errors).length > 0;
}

/**
 * The position of this button's call in the message, which is also where the
 * response holds its return value; -1 when the message is not for it.
 * `params` narrows a button to one row's call, e.g. retryFile(7); a function
 * is read when the call is sent, for a dialog that shows a different row
 * each time.
 */
function indexOfCall(message, method, params) {
    const expected = params === null ? null : JSON.stringify(typeof params === "function" ? params() : params);

    return Array.from(message.actions).findIndex(
        (action) => action.name === method && (expected === null || JSON.stringify(action.params) === expected),
    );
}

/**
 * Marks a Livewire message whose failure is shown beside its button, so the
 * app-wide error toast in livewire-interceptors.js leaves it alone.
 */
export function showErrorsInline(message) {
    message.errorsShownInline = true;
}

export function actionButton({
    method,
    params = null,
    closeEvent = null,
    successDelay = 600,
    closeFollowupDelay = 360,
    showsErrors = false,
    toast = null,
} = {}) {
    return {
        state: "idle",
        _resetTimer: null,
        _stopIntercepting: null,
        _destroyed: false,
        _closed: false,

        /**
         * The face classes. The error face only exists with `showsErrors`; it
         * stays until the next attempt so the message beside it can be read.
         */
        get faceClasses() {
            return {
                "action-button--loading": this.state === "loading",
                "action-button--success": this.state === "success",
                "action-button--error": this.state === "error",
            };
        },

        get busy() {
            return this.state === "loading" || this.state === "success";
        },

        /**
         * `toast` is the app-wide success message (the layout's notify
         * stack). A call that returns false did nothing, e.g. the token was
         * already gone: no done face and no toast, and a confirm dialog
         * closes at once since there is nothing left to confirm.
         */
        init() {
            this._stopIntercepting = this.$wire.interceptMessage(method, ({ message, onSuccess, onError, onFailure, onCancel }) => {
                if (this._destroyed) {
                    return;
                }

                const index = indexOfCall(message, method, params);
                if (index === -1) {
                    return;
                }

                if (showsErrors) {
                    showErrorsInline(message);
                }

                clearTimeout(this._resetTimer);
                this._closed = false;
                this.state = "loading";

                onSuccess(({ payload }) => {
                    if (hasValidationErrors(payload?.snapshot)) {
                        this.state = "idle";
                        return;
                    }

                    if (payload?.effects?.returns?.[index] === false) {
                        this.state = "idle";
                        this.close();
                        return;
                    }

                    this.state = "success";
                    if (toast) {
                        this.$dispatch("notify", { type: "success", message: toast });
                    }

                    this._resetTimer = setTimeout(() => {
                        if (!closeEvent) {
                            this.state = "idle";
                            return;
                        }

                        this.close();
                        this._resetTimer = setTimeout(() => {
                            this.state = "idle";
                        }, closeFollowupDelay);
                    }, successDelay);
                });

                onError(() => {
                    this.state = showsErrors ? "error" : "idle";
                });

                onFailure(() => {
                    this.state = showsErrors ? "error" : "idle";
                });

                onCancel?.(() => {
                    this.state = "idle";
                });
            });
        },

        close() {
            if (closeEvent) {
                this._closed = true;
                window.dispatchEvent(new CustomEvent(closeEvent));
            }
        },

        /**
         * Its dialog or form opened or closed: drop a failed face, and a done
         * face whose close has not run yet, so the next opening (maybe for
         * another row) starts clean and no late close event shuts it.
         */
        reset() {
            if (this.state === "error" || (this.state === "success" && !this._closed)) {
                clearTimeout(this._resetTimer);
                this.state = "idle";
            }
        },

        /**
         * Livewire 4.4.5 returns an unsubscribe function from interceptMessage
         * that calls a method its own WeakBag does not have (`delete`), so it
         * throws (Sentry SALESBYTE-28P). The `_destroyed` flag is what really
         * stops this button from reacting after its element is gone; the
         * unsubscribe is attempted only as a courtesy and never allowed to throw.
         */
        destroy() {
            clearTimeout(this._resetTimer);
            this._destroyed = true;
            try {
                this._stopIntercepting?.();
            } catch (error) {
                // Livewire's unsubscribe is broken in this version; the flag above covers us.
            }
        },
    };
}

document.addEventListener("alpine:init", () => {
    window.Alpine.data("actionButton", actionButton);
});
